// components/personalize/AIReviewStep.tsx
import React from 'react';
import AIBookMobile from './AIBookMobile';
import AIBook from './AIBook';
import { useRouter } from 'next/router';
import AIBookMobileGlobal from './AIBookMobileGlobal';

export default function AIReviewStep({
  isLoading,
  data,
  inputDedication,
  product,
  childName,
  name,
  dedicationImage,
  imageSelected,
  bookView,
  isProductRtlInBook,
  desktopRef,
  setStep,
  setActiveOption,
  setDedicationModal,
  productData,
  isEdit,
  isAIStory,
  isAIGenerating,
  aiLoadingStep,
  dedicationImageMode,
  aiStoryImage,
  croppedImage
}: any) {
  const { locale } = useRouter();

  const transformBookData = (data: any, isEditMode: boolean) => {
    if (!data) return null;
    
    // Create deep copies
    let pagesPDF = data?.pages?.pdf ? [...data.pages.pdf] : [];
    let coverPagesPDF = data?.cover_pages?.pdf ? [...data.cover_pages.pdf] : [];
    
    // Extract text arrays
    let coverPagesPredefinedTexts = data?.cover_pages_predefined_texts || [];
    let pagesPredefinedTexts = data?.pages_predefined_texts || [];
    let pagesDedicationTexts = data?.pages_dedication_texts || [];
    let coverPagesDedicationTexts = data?.cover_pages_dedication_texts || [];
    
    // 1. Process AI-generated pages FIRST (before any reversal)
    const aiGeneratedPages = pagesPredefinedTexts?.filter(
      (page: any) => page.ai_generated === true && page.image
    ) || [];
    
    // Replace page images with AI images
    aiGeneratedPages.forEach((aiPage: any) => {
      const pageNumber = parseInt(aiPage.page);
      if (!isNaN(pageNumber) && pageNumber >= 0 && pageNumber < pagesPDF.length) {
        pagesPDF[pageNumber] = aiPage.image;
      }
    });
    
    // 2. Process AI-generated cover pages
    const aiGeneratedCoverPages = coverPagesPredefinedTexts?.filter(
      (coverPage: any) => coverPage.ai_generated === true && coverPage.image
    ) || [];
    
    // Replace cover page images
    aiGeneratedCoverPages.forEach((aiCoverPage: any) => {
      const coverPageNumber = parseInt(aiCoverPage.page);
      if (!isNaN(coverPageNumber) && coverPageNumber >= 0 && coverPageNumber < coverPagesPDF.length) {
        coverPagesPDF[coverPageNumber] = aiCoverPage.image;
      }
    });
    
    if (isEdit === '1') {
      pagesPDF = [...pagesPDF].reverse();
      // ❗️Only reverse cover pages if your non-AI flow does the same
      // coverPagesPDF = [...coverPagesPDF].reverse();
    }

    return {
      pages: {
        pdf: pagesPDF
      },
      cover_pages: {
        pdf: coverPagesPDF
      },
      cover_pages_predefined_texts: coverPagesPredefinedTexts,
      pages_predefined_texts: pagesPredefinedTexts,
      pages_dedication_texts: pagesDedicationTexts,
      cover_pages_dedication_texts: coverPagesDedicationTexts,
      isEdit: isEditMode
    };
  };

  const bookData = transformBookData(data, isEdit);
  const bookIsLoading = isAIGenerating && !data;

  const MarketScope = isEdit === '1' 
    ? productData?.product?.marketScope || productData?.marketScope 
    : product?.market_scope;
  
  // Safely check if MarketScope includes 'IL'
  let isInIsrael = false;
  
  if (MarketScope) {
    try {
      let scopeArray: string[] = [];
      
      if (Array.isArray(MarketScope)) {
        scopeArray = MarketScope;
      } else if (typeof MarketScope === 'string') {
        try {
          const parsed = JSON.parse(MarketScope);
          if (Array.isArray(parsed)) {
            scopeArray = parsed;
          } else if (typeof parsed === 'string') {
            scopeArray = [parsed];
          }
        } catch {
          scopeArray = MarketScope.split(',').map((s: string) => s.trim());
        }
      }
      
      scopeArray = scopeArray.map((s: string) => s.trim().toUpperCase());
      isInIsrael = scopeArray.includes('IL');
    } catch (error) {
      console.error('Error processing MarketScope:', error);
      isInIsrael = false;
    }
  }

  // Set visible page count: 5 for AI stories
  const visiblePageCount = 4;

  return (
    <div className={`flex-1 overflow-y-auto px-2 ${bookView === 'single' ? 'py-6' : 'py-3'} space-y-6 bg-[#FFFCEB] hide-scrollbar`}>
      {bookView === 'single' ? (
        isInIsrael ? (
          <AIBookMobile
            key={`book-mobile-${bookView}-ai`}
            pages={bookData?.pages?.pdf}
            cover_pages={bookData?.cover_pages?.pdf}
            cover_pages_predefined_texts={bookData?.cover_pages_predefined_texts}
            inputDedication={inputDedication}
            product={product}
            pages_predefined_texts={bookData?.pages_predefined_texts}
            pages_dedication_texts={bookData?.pages_dedication_texts}
            setStep={setStep}
            setActiveOption={setActiveOption}
            setDedicationModal={setDedicationModal}
            name={childName}
            dedicationImage={dedicationImageMode === 'separate-image' ? croppedImage : null}
            imageSelected={imageSelected}
            bookView={bookView}
            isProductRtlInBook={isProductRtlInBook}
            isInIsrael={isInIsrael}
            visiblePageCount={visiblePageCount}
            isAIStory={isAIStory}
            isAIGenerating={isAIGenerating}
            aiLoadingStep={aiLoadingStep}
            dedicationImageMode={dedicationImageMode}
            aiStoryImage={aiStoryImage}
            isEdit={isEdit}
          />
        ) : (
          <AIBookMobileGlobal
            key={`book-mobile-${bookView}-ai-global`}
            pages={bookData?.pages?.pdf}
            cover_pages={bookData?.cover_pages?.pdf}
            cover_pages_predefined_texts={bookData?.cover_pages_predefined_texts}
            inputDedication={inputDedication}
            product={product}
            pages_predefined_texts={bookData?.pages_predefined_texts}
            pages_dedication_texts={bookData?.pages_dedication_texts}
            setStep={setStep}
            setActiveOption={setActiveOption}
            setDedicationModal={setDedicationModal}
            name={childName}
            dedicationImage={dedicationImageMode === 'separate-image' ? croppedImage : null}
            imageSelected={imageSelected}
            bookView={bookView}
            isProductRtlInBook={isProductRtlInBook}
            isInIsrael={isInIsrael}
            visiblePageCount={visiblePageCount}
            isAIStory={isAIStory}
            isAIGenerating={isAIGenerating}
            aiLoadingStep={aiLoadingStep}
            dedicationImageMode={dedicationImageMode}
            aiStoryImage={aiStoryImage}
            isEdit={isEdit}
          />
        )
      ) : (
        <AIBook
          key={`book-${bookView}-ai`}
          pages={bookData?.pages?.pdf}
          cover_pages={bookData?.cover_pages?.pdf}
          cover_pages_predefined_texts={bookData?.cover_pages_predefined_texts}
          inputDedication={inputDedication}
          product={product}
          pages_predefined_texts={bookData?.pages_predefined_texts}
          pages_dedication_texts={bookData?.pages_dedication_texts}
          desktopRef={desktopRef}
          dedicationImage={dedicationImageMode === 'separate-image' ? croppedImage : null}
          imageSelected={imageSelected}
          bookView={bookView}
          isProductRtlInBook={isProductRtlInBook}
          isInIsrael={isInIsrael}
          visiblePageCount={visiblePageCount}
          isAIStory={isAIStory}
          isAIGenerating={isAIGenerating}
          aiLoadingStep={aiLoadingStep}
          dedicationImageMode={dedicationImageMode}
          aiStoryImage={aiStoryImage}
          isEdit={isEdit}
        />
      )}
    </div>
  );
}